import { useEffect, useMemo, useState } from "react"
import { useIntegrations } from "../hooks/useIntegrations"
import { SiSlack, SiNotion, SiZapier, SiGooglesheets, SiHubspot } from "react-icons/si"
import { TbWebhook } from "react-icons/tb"
import {
    FiCalendar, FiCheckCircle, FiArrowRight, FiSearch,
    FiLock, FiAlertCircle, FiRefreshCw, FiX, FiActivity, FiZap,
} from "react-icons/fi"

const TIER_LABELS = { free: "Free", starter: "Starter", pro: "Pro", business: "Business", agency: "Agency" }
const TIER_ORDER = ["free", "starter", "pro", "business", "agency"]
const CATEGORY_ORDER = ["Productivity", "Communication", "CRM", "Developer Tools"]

// Real brand identity per provider — colored tile + white glyph, like a proper SaaS marketplace.
const BRAND = {
    webhook:       { Icon: TbWebhook,      bg: "bg-gradient-to-br from-slate-600 to-slate-800" },
    slack:         { Icon: SiSlack,        bg: "bg-gradient-to-br from-[#611f69] to-[#4A154B]" },
    google_sheets: { Icon: SiGooglesheets, bg: "bg-gradient-to-br from-[#1da462] to-[#0f9d58]" },
    notion:        { Icon: SiNotion,       bg: "bg-gradient-to-br from-gray-800 to-black" },
    zapier:        { Icon: SiZapier,       bg: "bg-gradient-to-br from-[#ff6537] to-[#ff4f00]" },
    hubspot:       { Icon: SiHubspot,      bg: "bg-gradient-to-br from-[#ff8f6b] to-[#ff5c35]" },
    calendly:      { Icon: FiCalendar,     bg: "bg-gradient-to-br from-[#3d7bff] to-[#006bff]" },
}

const STATUS_BADGE = {
    active:  { label: "Connected",       cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",   Icon: FiCheckCircle },
    error:   { label: "Needs attention", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",          Icon: FiAlertCircle },
    expired: { label: "Expired",         cls: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300", Icon: FiAlertCircle },
}

function tierAllows(userTier, requiredTier) {
    return TIER_ORDER.indexOf(userTier || "free") >= TIER_ORDER.indexOf(requiredTier || "free")
}

function BrandTile({ slug, name, size = "md" }) {
    const brand = BRAND[slug]
    const dims = size === "lg" ? "w-14 h-14 text-2xl rounded-2xl" : "w-12 h-12 text-xl rounded-xl"
    if (!brand) {
        return (
            <div className={`${dims} bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center font-bold text-white shadow-sm`}>
                {name?.[0]}
            </div>
        )
    }
    const { Icon, bg } = brand
    return (
        <div className={`${dims} ${bg} flex items-center justify-center text-white shadow-sm`}>
            <Icon />
        </div>
    )
}

// ── Webhook connect modal ─────────────────────────────────────────────────────

function WebhookModal({ onClose, onConnected }) {
    const { connectApiKey } = useIntegrations()
    const [url, setUrl] = useState("")
    const [secret, setSecret] = useState("")
    const [error, setError] = useState(null)
    const [saving, setSaving] = useState(false)

    const handleSave = async () => {
        if (!url.startsWith("https://")) { setError("URL must start with https://"); return }
        setSaving(true)
        setError(null)
        try {
            const apiKey = JSON.stringify({ url, secret: secret || undefined })
            const conn = await connectApiKey("webhook", apiKey)
            onConnected(conn)
        } catch (e) {
            setError(e.message)
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6 ring-1 ring-black/5" onClick={e => e.stopPropagation()}>
                <div className="flex items-center gap-3 mb-5">
                    <BrandTile slug="webhook" name="Webhook" />
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white leading-tight">Connect Webhook</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">POST responses to your endpoint</p>
                    </div>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Target URL <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="url"
                            value={url}
                            onChange={e => setUrl(e.target.value)}
                            placeholder="https://your-server.com/webhook"
                            className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            HMAC Signing Secret <span className="text-gray-400">(optional)</span>
                        </label>
                        <input
                            type="password"
                            value={secret}
                            onChange={e => setSecret(e.target.value)}
                            placeholder="We'll sign payloads with sha256"
                            className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                            We send <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">X-ContentFlashAI-Signature: sha256=…</code> on every request.
                        </p>
                    </div>
                    {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
                    <div className="flex gap-3 justify-end pt-1">
                        <button onClick={onClose} className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition">Cancel</button>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-5 py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition shadow-sm shadow-indigo-600/30"
                        >
                            {saving ? "Connecting…" : "Connect"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

// ── Connection detail drawer ──────────────────────────────────────────────────

function ConnectionDetail({ connectionId, providerName, providerSlug, onDisconnect, onClose }) {
    const { getConnectionDetail, disconnect, refreshConnection } = useIntegrations()
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [working, setWorking] = useState(false)

    useEffect(() => {
        getConnectionDetail(connectionId).then(setData).catch(console.error).finally(() => setLoading(false))
    }, [connectionId, getConnectionDetail])

    const handleDisconnect = async () => {
        if (!window.confirm(`Disconnect ${providerName}? This will disable form integrations using this connection.`)) return
        setWorking(true)
        try {
            await disconnect(data?.connection?.providerSlug, connectionId)
            onDisconnect()
        } catch (e) { alert(e.message) } finally { setWorking(false) }
    }

    const handleRefresh = async () => {
        setWorking(true)
        try {
            await refreshConnection(data?.connection?.providerSlug, connectionId)
            alert("Token refreshed successfully")
        } catch (e) { alert(e.message) } finally { setWorking(false) }
    }

    const badge = data && (STATUS_BADGE[data.connection.status] || { label: data.connection.status, cls: "" })

    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white dark:bg-gray-900 shadow-2xl w-full sm:max-w-md h-full overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="sticky top-0 bg-white/90 dark:bg-gray-900/90 backdrop-blur border-b border-gray-100 dark:border-gray-800 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <BrandTile slug={providerSlug} name={providerName} />
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white leading-tight">{providerName}</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Connection details</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><FiX /></button>
                </div>

                <div className="p-6">
                    {loading && <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>}

                    {!loading && data && (
                        <div className="space-y-5">
                            <div className="p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl text-sm space-y-2.5">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-500 dark:text-gray-400">Status</span>
                                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${badge.cls}`}>
                                        {badge.Icon && <badge.Icon className="w-3 h-3" />}{badge.label}
                                    </span>
                                </div>
                                {data.connection.credentialMetadata?.teamName && (
                                    <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">Workspace</span><span className="font-medium dark:text-white">{data.connection.credentialMetadata.teamName}</span></div>
                                )}
                                {data.connection.credentialMetadata?.account && (
                                    <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">Account</span><span className="font-medium dark:text-white">{data.connection.credentialMetadata.account}</span></div>
                                )}
                                <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">Connected</span><span className="font-medium dark:text-white">{new Date(data.connection.connectedAt).toLocaleDateString()}</span></div>
                            </div>

                            {data.connection.lastError && (
                                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-xl text-sm text-red-700 dark:text-red-400">
                                    <strong>Last error:</strong> {data.connection.lastError}
                                </div>
                            )}

                            <div className="flex gap-2">
                                {/* Only OAuth tokens that expire can be refreshed. Providers with
                                    non-expiring tokens (e.g. Slack bot tokens) have no refresh_token,
                                    so hide the button rather than surface a guaranteed error. */}
                                {data.connection.expiresAt && (
                                    <button onClick={handleRefresh} disabled={working} className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition">
                                        <FiRefreshCw className="w-4 h-4" /> Refresh
                                    </button>
                                )}
                                <button onClick={handleDisconnect} disabled={working} className="flex-1 px-3 py-2.5 text-sm font-medium bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/50 disabled:opacity-50 transition">
                                    Disconnect
                                </button>
                            </div>

                            <div>
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2"><FiActivity className="w-4 h-4" /> Recent activity</h4>
                                {data.logs?.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No activity yet.</p>}
                                <div className="space-y-1.5">
                                    {(data.logs || []).map(log => (
                                        <div key={log._id} className="flex items-center gap-3 text-xs p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/60">
                                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${log.status === "success" ? "bg-green-400" : "bg-red-400"}`} />
                                            <span className="font-mono text-gray-600 dark:text-gray-400 flex-1 truncate">{log.action}</span>
                                            <span className="text-gray-400 dark:text-gray-500">{log.latencyMs}ms</span>
                                            <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">{new Date(log.createdAt).toLocaleTimeString()}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

// ── Provider card ─────────────────────────────────────────────────────────────

function ProviderCard({ provider, userPlan, connecting, onConnect, onViewDetail }) {
    const connection = provider.connections?.[0]
    const hasConnection = !!connection
    const locked = !tierAllows(userPlan, provider.requiredTier)
    const comingSoon = provider.status === "coming_soon"
    const badge = hasConnection && (STATUS_BADGE[connection.status] || { label: connection.status, cls: "" })

    return (
        <div className={`group relative flex flex-col bg-white dark:bg-gray-800/80 rounded-2xl border border-gray-200 dark:border-gray-700/80 p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-gray-200/60 dark:hover:shadow-black/40 hover:border-gray-300 dark:hover:border-gray-600 ${locked ? "opacity-95" : ""}`}>
            <div className="flex items-start justify-between">
                <BrandTile slug={provider.slug} name={provider.name} />
                {hasConnection && badge && (
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${badge.cls}`}>
                        {badge.Icon && <badge.Icon className="w-3 h-3" />}{badge.label}
                    </span>
                )}
                {!hasConnection && comingSoon && (
                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full font-medium bg-gray-100 text-gray-500 dark:bg-gray-700/60 dark:text-gray-400">
                        Soon
                    </span>
                )}
                {!hasConnection && !comingSoon && locked && (
                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                        <FiLock className="w-3 h-3" /> {TIER_LABELS[provider.requiredTier]}
                    </span>
                )}
            </div>

            <div className="mt-4 flex-1">
                <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900 dark:text-white">{provider.name}</h3>
                    {provider.status === "beta" && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded font-medium uppercase tracking-wide">Beta</span>
                    )}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 leading-snug line-clamp-2">{provider.description}</p>
            </div>

            <div className="mt-4">
                {comingSoon ? (
                    <button disabled className="w-full py-2.5 text-sm text-gray-400 border border-gray-200 dark:border-gray-700 rounded-xl cursor-not-allowed">Coming soon</button>
                ) : locked ? (
                    <a href="/settings" className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/40 rounded-xl hover:bg-amber-100 dark:hover:bg-amber-900/30 transition">
                        <FiZap className="w-4 h-4" /> Upgrade to {TIER_LABELS[provider.requiredTier]}
                    </a>
                ) : connecting ? (
                    <button disabled className="w-full inline-flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-gray-500 border border-gray-200 dark:border-gray-700 rounded-xl">
                        <FiRefreshCw className="w-4 h-4 animate-spin" /> Connecting…
                    </button>
                ) : hasConnection ? (
                    <button
                        onClick={() => onViewDetail(connection._id, provider.name, provider.slug)}
                        className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/60 transition"
                    >
                        Manage
                    </button>
                ) : (
                    <button
                        onClick={() => onConnect(provider)}
                        className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition shadow-sm shadow-indigo-600/20 group-hover:shadow-indigo-600/30"
                    >
                        Connect <FiArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </button>
                )}
            </div>
        </div>
    )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function IntegrationsPage() {
    const { providers, userTier, loading, error, fetchProviders, connectOAuth } = useIntegrations()
    const [webhookModal, setWebhookModal] = useState(false)
    const [detailView, setDetailView] = useState(null)   // { connectionId, providerName, providerSlug }
    const [connectingSlug, setConnectingSlug] = useState(null)
    const [connectError, setConnectError] = useState(null)
    const [query, setQuery] = useState("")

    useEffect(() => { fetchProviders() }, [fetchProviders])

    // Safety net: while an OAuth connect is in flight, refetch when focus returns to this window
    // (i.e. the popup closed). Covers browsers where the popup's postMessage never reaches us,
    // so the connected state shows up without a manual reload.
    useEffect(() => {
        if (!connectingSlug) return
        const onReturn = () => { if (document.visibilityState !== "hidden") fetchProviders() }
        window.addEventListener("focus", onReturn)
        document.addEventListener("visibilitychange", onReturn)
        return () => {
            window.removeEventListener("focus", onReturn)
            document.removeEventListener("visibilitychange", onReturn)
        }
    }, [connectingSlug, fetchProviders])

    const handleConnect = async (provider) => {
        setConnectError(null)
        if (provider.slug === "webhook") { setWebhookModal(true); return }
        setConnectingSlug(provider.slug)
        try {
            await connectOAuth(provider.slug)
        } catch (e) {
            // The OAuth popup can complete server-side while its postMessage back to us is lost
            // (browsers sever window.opener across the cross-origin OAuth hop under COOP). In that
            // case the hook rejects with "popup closed" even though the connection succeeded — the
            // refetch in `finally` reveals the real state, so don't surface that as an error.
            if (!/popup closed/i.test(e.message || "")) setConnectError(e.message)
        } finally {
            // Always refetch so a completed connection is reflected without a manual page reload.
            await fetchProviders()
            setConnectingSlug(null)
        }
    }

    const connectedCount = useMemo(
        () => providers.filter(p => p.connections?.length > 0).length,
        [providers]
    )

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return providers
        return providers.filter(p =>
            p.name.toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q)
        )
    }, [providers, query])

    const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
        acc[cat] = filtered.filter(p => p.category === cat)
        return acc
    }, {})
    const hasResults = Object.values(grouped).some(arr => arr.length > 0)

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
            {/* Hero header */}
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-indigo-600 to-purple-600 p-8 mb-8 text-white shadow-lg shadow-indigo-600/20">
                <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
                <div className="absolute -bottom-16 -left-8 w-56 h-56 bg-purple-400/20 rounded-full blur-3xl" />
                <div className="relative">
                    <h1 className="text-3xl font-bold tracking-tight">Integrations</h1>
                    <p className="mt-2 text-indigo-100 max-w-xl">
                        Connect your favorite tools and automatically push form responses where your team already works.
                    </p>
                    <div className="mt-5 flex flex-wrap items-center gap-3 text-sm">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur font-medium">
                            <FiCheckCircle className="w-4 h-4" /> {connectedCount} connected
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur font-medium">
                            <FiZap className="w-4 h-4" /> {TIER_LABELS[userTier] || "Free"} plan
                        </span>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="relative mb-8 max-w-md">
                <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Search integrations…"
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
            </div>

            {connectError && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-700 dark:text-red-400 flex items-center justify-between">
                    <span className="flex items-center gap-2"><FiAlertCircle className="w-4 h-4" /> {connectError}</span>
                    <button onClick={() => setConnectError(null)} className="underline">Dismiss</button>
                </div>
            )}

            {loading && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="h-48 bg-gray-100 dark:bg-gray-800 rounded-2xl animate-pulse" />
                    ))}
                </div>
            )}

            {error && !loading && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl text-sm text-red-700 dark:text-red-400">{error}</div>
            )}

            {!loading && !error && !hasResults && (
                <div className="text-center py-16 text-gray-500 dark:text-gray-400">
                    <FiSearch className="w-8 h-8 mx-auto mb-3 opacity-40" />
                    No integrations match “{query}”.
                </div>
            )}

            {!loading && !error && Object.entries(grouped).map(([category, items]) => (
                items.length > 0 && (
                    <div key={category} className="mb-10">
                        <div className="flex items-center gap-2 mb-4">
                            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-200 uppercase tracking-wider">{category}</h2>
                            <span className="text-xs font-medium text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-full px-2 py-0.5">{items.length}</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {items.map(provider => (
                                <ProviderCard
                                    key={provider.slug}
                                    provider={provider}
                                    userPlan={userTier}
                                    connecting={connectingSlug === provider.slug}
                                    onConnect={handleConnect}
                                    onViewDetail={(id, name, slug) => setDetailView({ connectionId: id, providerName: name, providerSlug: slug })}
                                />
                            ))}
                        </div>
                    </div>
                )
            ))}

            {webhookModal && (
                <WebhookModal
                    onClose={() => setWebhookModal(false)}
                    onConnected={() => { setWebhookModal(false); fetchProviders() }}
                />
            )}

            {detailView && (
                <ConnectionDetail
                    connectionId={detailView.connectionId}
                    providerName={detailView.providerName}
                    providerSlug={detailView.providerSlug}
                    onDisconnect={() => { setDetailView(null); fetchProviders() }}
                    onClose={() => setDetailView(null)}
                />
            )}
        </div>
    )
}
